import { Routes } from '@angular/router';
import { authGuard, roleGuard, featureGuard, classGuard } from './core/auth/auth.guard';

export const routes: Routes = [
  {
    path: 'tenant',
    loadComponent: () => import('./shared/components/tenant-select/tenant-select').then(m => m.TenantSelect)
  },
  {
    path: 'error',
    loadComponent: () => import('./shared/components/tenant-error/tenant-error').then(m => m.TenantError)
  },
  {
    path: 'public/form/admissions',
    loadComponent: () => import('./features/admission/admission-form/admission-form').then(m => m.AdmissionForm)
  },
  {
    path: 'public/form/admissions/success',
    loadComponent: () => import('./features/admission/public-admission-success/public-admission-success').then(m => m.PublicAdmissionSuccess)
  },
  {
    path: 'auth',
    loadChildren: () => import('./auth/auth.module').then(m => m.AuthModule)
  },
  {
    path: 'login',
    redirectTo: '/auth/login',
    pathMatch: 'full'
  },
  {
    path: '',
    redirectTo: '/dashboard',
    pathMatch: 'full'
  },
  {
    path: '',
    loadComponent: () => import('./shared/layouts/main-layout/main-layout').then(m => m.MainLayout),
    canActivate: [authGuard],
    children: [
      {
        path: 'setup/success',
        canActivate: [roleGuard(['owner', 'admin'])],
        loadComponent: () => import('./features/setup/setup-success/setup-success').then(m => m.SetupSuccess)
      },
      {
        path: 'setup-checklist',
        canActivate: [roleGuard(['owner', 'admin'])],
        loadComponent: () => import('./features/setup/setup-checklist/setup-checklist').then(m => m.SetupChecklist)
      },
      {
        path: 'profile',
        loadComponent: () => import('./features/profile/profile').then(m => m.Profile)
      },
      {
        path: 'dashboard',
        canActivate: [roleGuard(['owner', 'admin', 'manager', 'teacher', 'parent'])],
        loadComponent: () => import('./features/dashboard/dashboard').then(m => m.Dashboard)
      },
      {
        path: 'admissions',
        canActivate: [roleGuard(['owner', 'admin', 'manager']), featureGuard('admissions')],
        loadChildren: () => import('./features/admission/admission.routes').then(m => m.ADMISSION_ROUTES)
      },
      {
        path: 'students',
        canActivate: [roleGuard(['owner', 'admin', 'manager', 'teacher', 'parent']), classGuard],
        loadChildren: () => import('./features/student/student.routes').then(m => m.STUDENT_ROUTES)
      },
      {
        path: 'classes',
        canActivate: [roleGuard(['owner', 'admin', 'manager', 'teacher']), classGuard],
        loadChildren: () => import('./features/class/class.routes').then(m => m.CLASS_ROUTES)
      },
      {
        path: 'teachers',
        canActivate: [roleGuard(['owner', 'admin', 'manager'])],
        loadChildren: () => import('./features/teacher/teacher.routes').then(m => m.TEACHER_ROUTES)
      },
      {
        path: 'payments',
        canActivate: [roleGuard(['owner', 'admin', 'manager', 'parent']), featureGuard('payments')],
        loadChildren: () => import('./features/payment/payment.routes').then(m => m.PAYMENT_ROUTES)
      },
      {
        path: 'payment-history',
        canActivate: [roleGuard(['parent']), classGuard, featureGuard('payments')],
        loadComponent: () => import('./features/payment/payment-history/payment-history').then(m => m.PaymentHistory)
      },
      {
        path: 'fee-dashboard',
        redirectTo: '/payments/child-fees',
        pathMatch: 'full'
      },
      {
        path: 'make-payment',
        canActivate: [roleGuard(['parent']), classGuard, featureGuard('payments')],
        loadComponent: () => import('./features/payment/payment-form/payment-form').then(m => m.PaymentForm)
      },
      {
        path: 'create-invoice',
        canActivate: [roleGuard(['teacher']), classGuard, featureGuard('payments')],
        loadComponent: () => import('./features/payment/teacher-invoice/teacher-invoice').then(m => m.TeacherInvoice)
      },
      {
        path: 'create-fee-request',
        redirectTo: '/payments/fees/raise',
        pathMatch: 'full'
      },
      {
        path: 'admin-fee-dashboard',
        canActivate: [roleGuard(['owner', 'admin', 'manager']), featureGuard('payments')],
        loadComponent: () => import('./features/payment/admin-fee-dashboard/admin-fee-dashboard').then(m => m.AdminFeeDashboard)
      },
      {
        path: 'grade-fee-mapping',
        canActivate: [roleGuard(['owner', 'admin', 'manager']), featureGuard('payments')],
        loadComponent: () => import('./features/payment/grade-fee-mapping/grade-fee-mapping').then(m => m.GradeFeeMapping)
      },
      {
        path: 'teacher-fee-dashboard',
        canActivate: [roleGuard(['teacher']), classGuard, featureGuard('payments')],
        loadComponent: () => import('./features/payment/teacher-fee-dashboard/teacher-fee-dashboard').then(m => m.TeacherFeeDashboard)
      },
      {
        path: 'fee-request-history',
        redirectTo: '/payments/fees/history',
        pathMatch: 'full'
      },
      {
        path: 'attendance',
        canActivate: [roleGuard(['owner', 'admin', 'manager', 'parent', 'teacher']), classGuard, featureGuard('attendance')],
        loadComponent: () => import('./features/attendance/attendance').then(m => m.Attendance)
      },
      {
        path: 'attendance-mark',
        canActivate: [roleGuard(['owner', 'admin', 'manager', 'teacher']), classGuard, featureGuard('attendance')],
        loadComponent: () => import('./features/attendance/attendance-mark/attendance-mark').then(m => m.AttendanceMark)
      },
      {
        path: 'attendance-overview',
        canActivate: [roleGuard(['owner', 'admin', 'manager']), featureGuard('attendance')],
        loadComponent: () => import('./features/attendance/attendance-overview/attendance-overview').then(m => m.AttendanceOverview)
      },
      {
        path: 'exam-timetable',
        canActivate: [roleGuard(['admin', 'manager', 'teacher']), classGuard],
        loadComponent: () => import('./features/exam/exam-timetable/exam-timetable').then(m => m.ExamTimetable)
      },
      {
        path: 'marks-entry',
        canActivate: [roleGuard(['teacher']), classGuard],
        loadComponent: () => import('./features/exam/marks-entry/marks-entry').then(m => m.MarksEntry)
      },
      {
        path: 'report-card',
        canActivate: [roleGuard(['teacher', 'parent']), classGuard],
        loadComponent: () => import('./features/exam/report-card/report-card').then(m => m.ReportCard)
      },
      {
        path: 'reports',
        canActivate: [roleGuard(['parent']), classGuard],
        loadComponent: () => import('./features/reports/reports').then(m => m.Reports)
      },
      {
        path: 'school-details',
        canActivate: [roleGuard(['owner', 'admin', 'parent'])],
        loadComponent: () => import('./features/school-details/school-details').then(m => m.SchoolDetails)
      },
      {
        path: 'my-teachers',
        canActivate: [roleGuard(['parent']), classGuard],
        loadComponent: () => import('./features/my-teachers/my-teachers').then(m => m.MyTeachers)
      },
      {
        path: 'messages',
        canActivate: [roleGuard(['owner', 'admin', 'manager', 'teacher', 'parent'])],
        loadChildren: () => import('./features/communication/message/message.routes').then(m => m.MESSAGE_ROUTES)
      },
      {
        path: 'announcements',
        canActivate: [roleGuard(['owner', 'admin', 'manager', 'teacher', 'parent']), featureGuard('announcements')],
        loadChildren: () => import('./features/communication/announcement/announcement.routes').then(m => m.ANNOUNCEMENT_ROUTES)
      },
      {
        path: 'activities',
        canActivate: [roleGuard(['owner', 'admin', 'manager', 'teacher', 'parent']), featureGuard('activities')],
        loadChildren: () => import('./features/activities/activities.routes').then(m => m.ACTIVITIES_ROUTES)
      },
      {
        path: 'busy-bees',
        canActivate: [roleGuard(['owner', 'admin', 'manager', 'teacher', 'parent']), featureGuard('busyBees')],
        loadChildren: () => import('./features/busy-bees/busy-bees.routes').then(m => m.BUSY_BEES_ROUTES)
      },
      {
        path: 'daily-reports',
        canActivate: [roleGuard(['owner', 'admin', 'manager', 'teacher', 'parent']), classGuard, featureGuard('dailyReports')],
        loadChildren: () => import('./features/daily-reports/daily-reports.routes').then(m => m.DAILY_REPORTS_ROUTES)
      },
      {
        path: 'data-management',
        canActivate: [roleGuard(['owner', 'admin'])],
        loadChildren: () => import('./features/data-management/data-management.routes').then(m => m.DATA_MANAGEMENT_ROUTES)
      },
      {
        path: 'settings',
        canActivate: [roleGuard(['owner', 'admin'])],
        loadComponent: () => import('./features/settings/settings').then(m => m.Settings),
        children: [
          {
            path: '',
            redirectTo: 'app-mode',
            pathMatch: 'full'
          },
          {
            path: 'app-mode',
            loadComponent: () => import('./features/settings/app-mode-settings/app-mode-settings').then(m => m.AppModeSettings)
          },
          {
            path: 'admin',
            loadComponent: () => import('./features/settings/admin-settings/admin-settings').then(m => m.AdminSettings)
          },
          {
            path: 'admission-form',
            redirectTo: 'admission',
            pathMatch: 'full'
          },
          {
            path: 'admission',
            loadComponent: () => import('./features/settings/admission-settings/admission-settings').then(m => m.AdmissionSettingsComponent)
          },
          {
            path: 'admissions',
            redirectTo: 'admission',
            pathMatch: 'full'
          },
          {
            path: 'features',
            loadComponent: () => import('./features/settings/feature-settings/feature-settings').then(m => m.FeatureSettings)
          },
          {
            path: 'daily-reports',
            loadComponent: () => import('./features/settings/daily-report-settings/daily-report-settings').then(m => m.DailyReportSettings)
          },
          {
            path: 'class-config',
            loadComponent: () => import('./features/settings/class-config-settings/class-config-settings').then(m => m.ClassConfigSettings)
          },
          {
            path: 'subject-config',
            loadComponent: () => import('./features/settings/subject-config-settings/subject-config-settings').then(m => m.SubjectConfigSettings)
          },
          {
            path: 'branding',
            loadComponent: () => import('./features/settings/branding-settings/branding-settings').then(m => m.BrandingSettings)
          },
          {
            path: 'fees-billing',
            loadComponent: () => import('./features/settings/fees-billing-settings/fees-billing-settings').then(m => m.FeesBillingSettings)
          },
          {
            path: 'academics',
            loadComponent: () => import('./features/settings/academics-settings/academics-settings').then(m => m.AcademicsSettings)
          },
          {
            path: 'theme',
            loadComponent: () => import('./features/settings/theme-settings/theme-settings').then(m => m.ThemeSettings)
          },
          {
            path: 'school-profile',
            loadComponent: () => import('./features/settings/school-profile-settings').then(m => m.SchoolProfileSettings)
          },
          {
            path: 'custom-domain',
            loadComponent: () => import('./features/settings/custom-domain-settings/custom-domain-settings').then(m => m.CustomDomainSettings)
          },
          {
            path: 'payment-gateway',
            loadComponent: () => import('./features/payment/gateway-config/gateway-setup').then(m => m.GatewaySetupComponent)
          },
          {
            path: 'fee-types',
            loadComponent: () => import('./features/settings/fee-types-settings/fee-types-settings').then(m => m.FeeTypesSettings)
          },
      {
        path: 'class-fees',
        loadComponent: () => import('./features/settings/class-fee-mapping-settings/class-fee-mapping-settings').then(m => m.ClassFeeMappingSettings)
      },
      {
        path: 'agreements',
        loadComponent: () => import('./features/settings/agreement-settings/agreement-settings').then(m => m.AgreementSettings)
      }
        ]
      },
      {
        path: 'feature-requests',
        canActivate: [roleGuard(['owner', 'admin', 'manager'])],
        loadChildren: () => import('./features/feature-request/feature-request.routes').then(m => m.FEATURE_REQUEST_ROUTES)
      }
    ]
  },
  {
    path: '**',
    redirectTo: '/dashboard'
  }
];
